import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { catalogServiceRepository } from "../repositories/catalog-service.repository";
const CACHE_DURATION = 5 * 60 * 1000;
export const useCatalogServiceStore = defineStore("catalogService", () => {
    const services = ref([]);
    const currentService = ref(null);
    const isLoading = ref(false);
    const error = ref(null);
    const lastFetched = ref(null);
    let scopeVersion = 0;
    const serviceById = computed(() => {
        return (id) => services.value.find((service) => service.id === id);
    });
    const isCacheValid = computed(() => {
        if (!lastFetched.value)
            return false;
        return Date.now() - lastFetched.value.getTime() < CACHE_DURATION;
    });
    async function fetchServices(forceRefresh = false, params) {
        const hasFilters = Boolean(params?.search ||
            params?.providerId != null ||
            params?.categoryId != null);
        if (!forceRefresh && isCacheValid.value && !hasFilters) {
            return services.value;
        }
        const version = scopeVersion;
        isLoading.value = true;
        error.value = null;
        try {
            const data = await catalogServiceRepository.getAll(params);
            if (version !== scopeVersion)
                throw new Error("tenant_scope_changed");
            services.value = data;
            lastFetched.value = new Date();
            return data;
        }
        catch (err) {
            if (version !== scopeVersion)
                throw err;
            error.value =
                err instanceof Error ? err.message : "Error fetching catalog services";
            throw err;
        }
        finally {
            if (version === scopeVersion)
                isLoading.value = false;
        }
    }
    async function fetchServiceById(id) {
        const version = scopeVersion;
        isLoading.value = true;
        error.value = null;
        try {
            const data = await catalogServiceRepository.getById(id);
            if (version !== scopeVersion)
                throw new Error("tenant_scope_changed");
            currentService.value = data;
            const index = services.value.findIndex((service) => service.id === id);
            if (index !== -1) {
                services.value[index] = data;
            }
            return data;
        }
        catch (err) {
            if (version !== scopeVersion)
                throw err;
            error.value =
                err instanceof Error ? err.message : "Error fetching catalog service";
            throw err;
        }
        finally {
            if (version === scopeVersion)
                isLoading.value = false;
        }
    }
    function invalidateCache() {
        lastFetched.value = null;
    }
    function $reset() {
        scopeVersion++;
        services.value = [];
        currentService.value = null;
        isLoading.value = false;
        error.value = null;
        lastFetched.value = null;
    }
    return {
        services,
        currentService,
        isLoading,
        error,
        lastFetched,
        serviceById,
        isCacheValid,
        fetchServices,
        fetchServiceById,
        invalidateCache,
        $reset,
    };
});
